import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { normalizeStyle } from "../utils/sectionStyle";
import { sectionTemplates } from "../../../data/database";
import NavSectionEditor from "./sections/NavSectionEditor";

function useDebouncedCallback(callback, delay = 120) {
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return useCallback(
    (...args) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => callback?.(...args), delay);
    },
    [callback, delay]
  );
}

export default function NavEditorModal({ nav, onPatch, onClose, uploadImage, t = (k, f) => f }) {
  const [activeTab, setActiveTab] = useState("content");

  const templateKey = nav?.template || "template1";
  const tpl = sectionTemplates?.navsection?.[templateKey];

  const tplCfg = tpl?.config || {};
  const tplStyle = tpl?.style || {};

  const [draft, setDraft] = useState(() => ({
    ...nav,
    config: { ...tplCfg, ...(nav?.config || {}) },
    style: normalizeStyle({ ...tplStyle, ...(nav?.style || {}) }),
  }));

  const navIdentity = nav?.id || nav?._id || "nav";
  const lastNavIdentityRef = useRef(navIdentity);

  const debouncedPatch = useDebouncedCallback(onPatch, 120);

  useEffect(() => {
    if (lastNavIdentityRef.current !== navIdentity) {
      setDraft({
        ...nav,
        config: { ...tplCfg, ...(nav?.config || {}) },
        style: normalizeStyle({ ...tplStyle, ...(nav?.style || {}) }),
      });
      lastNavIdentityRef.current = navIdentity;
    }
  }, [nav, navIdentity, tplCfg, tplStyle]);

  const handleInputChange = useCallback((field, value) => {
    setDraft((prev) => ({
      ...prev,
      config: { ...(prev.config || {}), [field]: value },
    }));
    debouncedPatch?.({ config: { [field]: value } });
  }, [debouncedPatch]);

  const handleStyleChange = useCallback((field, value) => {
    setDraft((prev) => ({
      ...prev,
      style: { ...(prev.style || {}), [field]: value },
    }));
    debouncedPatch?.({ style: { [field]: value } });
  }, [debouncedPatch]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border flex flex-col">
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <div className="text-lg font-bold text-gray-900">
              {t("navModal.title", "Edit Navigation")}
            </div>
            <div className="text-sm text-gray-500">
              {t("navModal.subtitle", "Navigation renders once at the top (like footer).")}
            </div>
          </div>
          <button onClick={onClose} className="text-2xl text-gray-400 hover:text-gray-600">
            ×
          </button>
        </div>

        <div className="px-6 pt-4">
          <div className="inline-flex gap-1 p-1 bg-gray-100 rounded-xl">
            <button
              type="button"
              onClick={() => setActiveTab("content")}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                activeTab === "content" ? "bg-white shadow ring-1 ring-gray-200" : "text-gray-600"
              }`}
            >
              {t("navModal.tabs.content", "Content")}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("design")}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                activeTab === "design" ? "bg-white shadow ring-1 ring-gray-200" : "text-gray-600"
              }`}
            >
              {t("navModal.tabs.design", "Design")}
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-6 overflow-y-auto overscroll-contain">
          {activeTab === "content" ? (
            <NavSectionEditor
              config={draft.config}
              onChange={handleInputChange}
              uploadImage={uploadImage}
              t={t}
            />
          ) : (
            <div className="space-y-4">
              <div className="font-semibold text-gray-900">
                {t("navModal.design.title", "Navigation Colors")}
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-700 mb-1">
                    {t("navModal.design.background", "Background")}
                  </div>
                  <input
                    type="color"
                    value={draft.style?.bgColor || "#0B1220"}
                    onChange={(e) => handleStyleChange("bgColor", e.target.value)}
                    className="h-10 w-20 rounded border"
                  />
                </div>

                <div>
                  <div className="text-sm text-gray-700 mb-1">
                    {t("navModal.design.textColor", "Text Color")}
                  </div>
                  <input
                    type="color"
                    value={draft.style?.textColor || "#ffffff"}
                    onChange={(e) => handleStyleChange("textColor", e.target.value)}
                    className="h-10 w-20 rounded border"
                  />
                </div>

                <div>
                  <div className="text-sm text-gray-700 mb-1">
                    {t("navModal.design.linkColor", "Link Color")}
                  </div>
                  <input
                    type="color"
                    value={draft.style?.linkColor || "#cbd5e1"}
                    onChange={(e) => handleStyleChange("linkColor", e.target.value)}
                    className="h-10 w-20 rounded border"
                  />
                </div>

                <div>
                  <div className="text-sm text-gray-700 mb-1">
                    {t("navModal.design.buttonBg", "Button Background")}
                  </div>
                  <input
                    type="color"
                    value={draft.style?.buttonBgColor || "#ffffff"}
                    onChange={(e) => handleStyleChange("buttonBgColor", e.target.value)}
                    className="h-10 w-20 rounded border"
                  />
                </div>

                <div>
                  <div className="text-sm text-gray-700 mb-1">
                    {t("navModal.design.buttonText", "Button Text")}
                  </div>
                  <input
                    type="color"
                    value={draft.style?.buttonTextColor || "#0B1220"}
                    onChange={(e) => handleStyleChange("buttonTextColor", e.target.value)}
                    className="h-10 w-20 rounded border"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border hover:bg-white">
            {t("navModal.done", "Done")}
          </button>
        </div>
      </div>
    </div>
  );
}